import { useRef, useEffect } from "react";
import "../styles/education.css";
import educationBg from "../photos/Resume04.jpg";
import faithPanel  from "../photos/FAITH00.jpg";
import mshsPanel   from "../photos/MSHS00.jpg";
import faithBg     from "../photos/FAITH02.jpg";
import mshsBg      from "../photos/MSHS02.jpg";
import faith01     from "../photos/FAITH01.jpg";
import faith03     from "../photos/FAITH03.jpeg";
import mshs03      from "../photos/MSHS03.png";
import mshs04      from "../photos/MSHS04.jpeg";

const faithTags = [
  "Computer Engineering",
  "Microelectronics",
  "Seminars and Workshops",
  "Software Development",
  "Hardware Design",
  "Digital Systems",
  "CIDR Conference"
];

const mshsTags = [
  "STEM Strand",
  "Robotics",
  "Capstone Project",
  "Mathematics",
  "Physics",
  "Chemistry",
  "Laboratory Research",
  "Engineering Principles",
];

const timelineEvents = [
  {
    year: "2021",
    title: "Enrolled in Senior High School",
    desc: "Joined Malvar Senior High School under the STEM strand — a decision that would ignite a lifelong curiosity for engineering.",
    active: true,
  },
  {
    year: "2022",
    title: "Capstone Project",
    desc: "Collaborated with like-minded peers to bring an engineering concept to life, cementing my conviction to pursue Computer Engineering.",
    active: false,
  },
  {
    year: "2023",
    title: "Graduated with Honors",
    desc: "Completed Senior High School with tears in the eyes due to a missed higher achievement opportunity. However, I earned a place and scholarship at FAITH Colleges to pursue BS Computer Engineering.",
    active: true,
  },
  {
    year: "2024 (To Date)",
    title: "Dean's & President's Lister",
    desc: "Recognized for academic excellence across consecutive semesters while actively joining seminars and industry-facing workshops.",
    active: false,
  },
  {
    year: "2025",
    title: "OOP Project Lead",
    desc: "The highlight of the year was leading a team project in Object-Oriented Programming, where we designed and implemented a photoboot software project called Piktyur Piktyur! This project gained the highest rating among all projects.",
    active: true,
  },
  {
    year: "2026",
    title: "Microelectronics",
    desc: "This year, I am thriving and still continuing more projects related to microelectronics. This field has become a major focus of my academic journey, and I am eager to deepen my knowledge and skills in this area through hands-on projects and research opportunities.",
    active: true,
  },
];

/* ─── Intersection Observer hook ─── */

function useReveal(selector = ".edu-reveal", options = {}) {
  useEffect(() => {
    const nodes = document.querySelectorAll(selector);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px", ...options }
    );
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [selector]);
}

/* Staggered tag reveal */
function useTagReveal() {
  useEffect(() => {
    const tags = document.querySelectorAll(".edu-spec-tag");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const siblings = e.target.closest(".edu-section__spec-tags")
              ?.querySelectorAll(".edu-spec-tag") ?? [];
            siblings.forEach((tag, i) => {
              setTimeout(() => tag.classList.add("is-visible"), i * 55);
            });
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    tags.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, []);
}

/* ─── Stats strip ─── */
function StatsStrip({ stats }) {
  return (
    <div className="edu-stats edu-reveal">
      {stats.map(({ value, label }) => (
        <div key={label} className="edu-stat">
          <span className="edu-stat__value">{value}</span>
          <span className="edu-stat__label">{label}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── Story block ─── */
function StoryBlock({ text, label = "— In My Own Words —", author }) {
  return (
    <div className="edu-story edu-reveal edu-reveal--delay-2">
      <p className="edu-story__label">{label}</p>
      <p className="edu-story__text">"{text}"</p>
      {author && <p className="edu-story__author">— {author}</p>}
    </div>
  );
}

/* ─── Timeline ─── */
function Timeline() {
  return (
    <div className="edu-timeline edu-reveal">
      <p className="edu-timeline__heading">— My Journey —</p>
      <div className="edu-timeline__track">
        {timelineEvents.map((ev, i) => (
          <div key={i} className="edu-timeline__item">
            <div className={`edu-timeline__dot ${ev.active ? "" : "edu-timeline__dot--dim"}`} />
            <div className="edu-timeline__card">
              <span className="edu-timeline__year">{ev.year}</span>
              <p className="edu-timeline__title">{ev.title}</p>
              <p className="edu-timeline__desc">{ev.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── School Section ─── */
function SchoolSection({
  sectionRef, tag, school, degree, period,
  bgPhoto, bgText, bodyLeft, mission, vision,
  rightPhoto1, rightPhoto2,
  specializations, specializationText,
  stats, storyQuote, storyAuthor,
}) {
  return (
    <section className="edu-section" ref={sectionRef}>

      {/* Banner */}
      <div className="edu-section__banner edu-reveal">
        <img src={bgPhoto} alt={bgText} className="edu-section__banner-photo" />
        <div className="edu-section__banner-overlay" />
        <h2 className="edu-section__banner-text">{bgText}</h2>
      </div>

      {/* Header */}
      <div className="edu-section__header edu-reveal edu-reveal--delay-1">
        <p className="edu-section__tag">{tag}</p>
        <h3 className="edu-section__school">{school}</h3>
        <p className="edu-section__degree">{degree}</p>
        <p className="edu-section__period">{period}</p>
      </div>

      {/* Stats strip */}
      {stats && <StatsStrip stats={stats} />}

      {/* Two-column body */}
      <div className="edu-section__body-wrap">

        {/* Left */}
        <div className="edu-section__left">
          <p className="edu-section__body edu-reveal edu-reveal--delay-1">{bodyLeft}</p>

          {/* Story block — optional personal quote area */}
          {storyQuote && (
            <StoryBlock text={storyQuote} author={storyAuthor} />
          )}

          <div
            className="edu-section__mv edu-reveal edu-reveal--delay-2"
            data-layout={tag === "Tertiary School" ? "combined" : "split"}
          >
            {tag === "Tertiary School" ? (
              <div className="edu-section__mv-item edu-section__mv-item--combined">
                <p className="edu-section__mv-label"> — Vision & Mission —</p>
                <p className="edu-section__mv-text">{mission}</p>
                <p className="edu-section__mv-text" style={{ marginTop: "12px" }}>{vision}</p>
              </div>
            ) : (
              <>
                <div className="edu-section__mv-item">
                  <p className="edu-section__mv-label"> — Mission — </p>
                  <p className="edu-section__mv-text">{mission}</p>
                </div>
                <div className="edu-section__mv-item">
                  <p className="edu-section__mv-label"> — Vision — </p>
                  <p className="edu-section__mv-text">{vision}</p>
                </div>
              </>
            )}
          </div>

          <div className="edu-section__spec edu-reveal edu-reveal--delay-3">
            <p className="edu-section__spec-label">— Specialization Interests —</p>
            {specializationText && (
              <p className="edu-section__spec-text">{specializationText}</p>
            )}
            <div className="edu-section__spec-tags">
              {specializations.map((s) => (
                <div key={s} className="edu-spec-tag">
                  <span className="edu-spec-tag__text">{s}</span>
                  <div className="edu-spec-tag__hover">
                    <span>{s}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Vertical divider */}
        <div className="edu-section__divider" />

        {/* Right */}
        <div className="edu-section__right edu-reveal edu-reveal--delay-2">
          <img src={rightPhoto1} alt="School photo 1" className="edu-section__right-photo" />
          <img src={rightPhoto2} alt="School photo 2" className="edu-section__right-photo" />
          <div className="edu-section__badge">
            <span className="edu-section__badge-text">
              {tag === "Tertiary School"
                ? "BS Computer Engineering · FAITH Colleges · 2023–2027"
                : "STEM Strand · Malvar SHS · 2021–2023"}
            </span>
          </div>
        </div>

      </div>
    </section>
  );
}

/* ─── Main Export ─── */
export default function Education() {
  const tertiaryRef = useRef(null);
  const srhsRef     = useRef(null);

  useReveal();
  useTagReveal();

  const scrollTo = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="edu-page">

      {/* ════ HERO — Semicircle ════ */}
      <div className="edu-hero">
        <h1 className="edu-hero__title">My Academic<br />Pursuit</h1>

        <div className="edu-semicircle-wrap">
          {/*
            THE FIX: clip-path is applied directly on .edu-semicircle in CSS.
            This ensures hover panels only activate inside the semicircle boundary.
          */}
          <div className="edu-semicircle">

            {/* Base photo */}
            <img src={educationBg} alt="Education" className="edu-semicircle__photo" />

            {/* Vignette */}
            <div className="edu-semicircle__vignette" />

            {/* Left hover panel */}
            <div
              className="edu-hover-panel edu-hover-panel--left"
              onClick={() => scrollTo(tertiaryRef)}
            >
              <div className="edu-hover-panel__expand edu-hover-panel__expand--left">
                <img src={faithPanel} alt="FAITH" className="edu-hover-panel__expand-photo" />
                <div className="edu-hover-panel__expand-fade edu-hover-panel__expand-fade--left" />
              </div>
              <div className="edu-hover-panel__content">
                <p className="edu-hover-panel__tag">Tertiary School</p>
                <p className="edu-hover-panel__name">
                  First Asia Institute of<br />Technology and Humanities
                </p>
                <p className="edu-hover-panel__hint">↓ Scroll to section</p>
              </div>
            </div>

            {/* Right hover panel */}
            <div
              className="edu-hover-panel edu-hover-panel--right"
              onClick={() => scrollTo(srhsRef)}
            >
              <div className="edu-hover-panel__expand edu-hover-panel__expand--right">
                <img src={mshsPanel} alt="MSHS" className="edu-hover-panel__expand-photo" />
                <div className="edu-hover-panel__expand-fade edu-hover-panel__expand-fade--right" />
              </div>
              <div className="edu-hover-panel__content">
                <p className="edu-hover-panel__tag">Senior High School</p>
                <p className="edu-hover-panel__name">
                  Malvar Senior<br />High School
                </p>
                <p className="edu-hover-panel__hint">↓ Scroll to section</p>
              </div>
            </div>

            {/* Center divider */}
            <div className="edu-semicircle__divider" />

          </div>
        </div>

        {/* Labels below semicircle */}
        <div className="edu-hero__labels">
          <span className="edu-hero__label">FAITH Colleges</span>
          <span className="edu-hero__label">Malvar SHS</span>
        </div>
      </div>

      {/* ════ SECTION DIVIDER ════ */}
      <div className="edu-section-divider edu-reveal" />

      {/* ════ TERTIARY SECTION ════ */}
      <SchoolSection
        sectionRef={tertiaryRef}
        tag="Tertiary School"
        school="First Asia Institute of Technology and Humanities"
        degree="BS Computer Engineering — Major in Microelectronics (Junior Year)"
        period="2023 – 2027"
        bgPhoto={faithBg}
        bgText="FAITH"
        bodyLeft="Currently pursuing a Bachelor of Science in Computer Engineering at FAITH Colleges. Consistently recognized as a Dean's and President's Lister, driven by a deep passion for software development, hardware design, and data science. Actively engaged in academic projects that bridge theory and real-world application."
        mission="Transforming Ourselves, Transforming Our World"
        vision="We are an academic community with a strong passion for excellence in education"
        rightPhoto1={faith01}
        rightPhoto2={faith03}
        specializations={faithTags}
        specializationText="What interests me most about FAITH's Computer Engineering program is the opportunity to learn outside the institution through seminars, workshops, and industry collaborations. Together with my classmates, I am able to learn the world outside that co-exists alongside academe - the real world. There are a lot of projects and technologies that piqued my interest, especially as an aspiring computer engineer. I'm eager to deepen my knowledge in microelectronics and software development, while also exploring emerging fields like data science and digital systems design."
        stats={[
          { value: "Junior Year", label: "3rd Year Student" },
          { value: "3×", label: "Pres. Lister" },
          { value: "2x", label: "Dean's Lister" },
          { value: "2027", label: "Expected Graduation" },
        ]}
        storyQuote="College taught me that engineering is not just about solving problems — it's about asking the right questions. Every lab, every seminar, every late night with in front of my computer is a reminder of why I chose this path."
        storyAuthor="on being a Computer Engineering student"
      />

      <div className="edu-section-divider edu-reveal" />

      <Timeline />

      <div className="edu-section-divider edu-reveal" />

      <SchoolSection
        sectionRef={srhsRef}
        tag="Senior High School"
        school="Malvar Senior High School"
        degree="STEM Strand — Science, Technology, Engineering, and Mathematics"
        period="2021 – 2023"
        bgPhoto={mshsBg}
        bgText="MSHS"
        bodyLeft="Graduated from Malvar Senior High School under the STEM strand, building a strong academic foundation in mathematics, science, and engineering principles. Demonstrated consistent academic excellence and leadership throughout the program."
        mission="A learner-centered public institution"
        vision="A quality complete basic education"
        rightPhoto1={mshs03}
        rightPhoto2={mshs04}
        specializations={mshsTags}
        specializationText="My experience in the STEM strand at Malvar SHS was memorable, nostalgic, and full of lessons that I still carry with me today. It was in senior high school where I first discovered my passion for engineering and technology, and where I laid the groundwork for my future academic and career pursuits. Especially during our capstone project, that time was when I was able to connect with people that have the same vision as me. They were what I considered solid and nostalgic form of bliss. The rigorous curriculum challenged me to think critically and creatively, while the supportive community of teachers and peers fostered a collaborative learning environment. I am grateful for the opportunities and experiences that Malvar SHS provided, and I look back on my time there with fondness and pride."
        stats={[
          { value: "STEM", label: "Strand" },
          { value: "2yr", label: "Program" },
          { value: "With Honor", label: "Academic Excellence" },
          { value: "2023", label: "Graduated" },
        ]}
        /* ── Your personal story quote — edit this ── */
        storyQuote="Senior high school was the first time I truly felt like an engineer. Our capstone project wasn't just a school requirement — it was proof that ideas, when shared with the right people, can become something real."
        storyAuthor="on Malvar SHS and the capstone"
      />

    </div>
  );
}